import { useEffect, useState } from 'react'
import type { Member, MemberDay } from '@domain/state.ts'
import { es } from '../strings/es.ts'

const GRACE_MS = 3 * 60_000

/**
 * The countdown.
 *
 * It compares against a stored end instant and never counts down internally: a
 * web app in the background is suspended, so anything that ticks comes back
 * wrong. Closing before the time runs out is a task of its own, validated by an
 * adult, because the app cannot see the game being shut.
 */
export function TimerScreen({
  member,
  day,
  accent,
  onStart,
  onBack,
}: {
  member: Member
  day: MemberDay
  accent: string
  onStart: (minutes: number) => void
  onBack: () => void
}) {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const id = setInterval(() => {
      setNow(Date.now())
    }, 1000)
    const onVisible = (): void => {
      setNow(Date.now())
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearInterval(id)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  const total = day.chestMinutes + day.extraMinutes
  const endsAt = day.timerEndsAt === undefined ? null : Date.parse(day.timerEndsAt)
  const remaining = endsAt === null ? null : endsAt - now
  const inGrace = remaining !== null && remaining <= 0 && remaining > -GRACE_MS
  const over = remaining !== null && remaining <= -GRACE_MS

  const minutes = remaining === null ? 0 : Math.max(0, Math.ceil(remaining / 60_000))
  const seconds = remaining === null ? 0 : Math.max(0, Math.ceil(remaining / 1000) % 60)
  const fraction =
    remaining === null || total === 0 ? 0 : Math.max(0, Math.min(1, remaining / (total * 60_000)))

  const size = 260
  const radius = 110
  const circumference = 2 * Math.PI * radius

  return (
    <div className="mx-auto max-w-2xl px-5 pt-5 pb-12">
      <button type="button" onClick={onBack} className="text-muted mb-5 text-sm font-bold">
        {es.today.back}
      </button>

      <h1 className="mb-8 text-3xl font-extrabold tracking-tight">⏱️ {es.timer.title}</h1>

      <div className="flex flex-col items-center">
        <svg
          viewBox={`0 0 ${size} ${size}`}
          className="w-full max-w-xs"
          role="img"
          aria-label={
            remaining === null
              ? es.timer.notStarted
              : over
                ? es.timer.finished
                : `${es.timer.remaining} ${es.timer.minutes(minutes)}`
          }
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="#d6ddd9"
            strokeWidth="18"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={over ? '#aa4227' : accent}
            strokeWidth="18"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={circumference * (1 - fraction)}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
          <text
            x={size / 2}
            y={size / 2 + 14}
            textAnchor="middle"
            fontSize="52"
            fontWeight="800"
            fill="currentColor"
          >
            {remaining === null ? '—' : `${minutes}:${String(seconds).padStart(2, '0')}`}
          </text>
        </svg>

        <div className="mt-8 w-full max-w-xs text-center">
          {remaining === null ? (
            <>
              <p className="text-muted m-0 mb-4">{es.timer.notStarted}</p>
              <button
                type="button"
                disabled={total === 0}
                onClick={() => {
                  onStart(total)
                }}
                className="w-full rounded-xl px-6 py-4 text-lg font-extrabold text-white disabled:opacity-40"
                style={{ background: accent }}
              >
                {es.timer.start} · {es.timer.minutes(total)}
              </button>
            </>
          ) : inGrace ? (
            <p className="bg-gold-soft text-gold m-0 rounded-lg p-4 font-bold">
              {es.timer.lastGame}
            </p>
          ) : over ? (
            <p className="bg-coral-soft text-coral m-0 rounded-lg p-4 font-bold">
              {es.timer.finished}
            </p>
          ) : (
            <p className="text-muted m-0 font-bold">{es.timer.remaining}</p>
          )}
        </div>
      </div>

      <p className="text-muted mt-10 text-center text-sm">
        {member.name} · {es.timer.minutes(total)}
      </p>
    </div>
  )
}
